// src/components/NotificationBell.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { tasksApi, TaskNotification } from '../api/tasks';
import { useUserInfo } from '../hooks/useUserInfo';
import {
  bulkHydrate,
  markRead,
  markAllRead,
  selectNotifications,
  selectUnreadCount,
  selectNotificationsHydrated,
} from '../redux/slices/notifications';

interface NotificationBellProps {
  onUnreadCountChange?: (count: number) => void;
}

export function NotificationBell({ onUnreadCountChange }: NotificationBellProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const hydrated = useSelector(selectNotificationsHydrated);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { getUserName } = useUserInfo();

  // Hydrate ONCE on mount. Subsequent updates arrive via WebSocket
  // (Chat.tsx dispatches pushNotification on `task_notification` frames).
  useEffect(() => {
    if (hydrated) return;
    setLoading(true);
    tasksApi.getNotifications(true, 50)
      .then(response => {
        dispatch(bulkHydrate(response.data));
      })
      .catch(error => {
        console.error('Failed to load notifications:', error);
      })
      .finally(() => setLoading(false));
  }, [dispatch, hydrated]);

  // Click-outside (unchanged from previous behaviour, no interval to clean up).
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Bubble unread count up to parent on every change (replaces old manual calls).
  useEffect(() => {
    if (onUnreadCountChange) onUnreadCountChange(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  const handleMarkRead = async (notificationId: string) => {
    dispatch(markRead(notificationId));     // optimistic
    try {
      await tasksApi.markNotificationRead(notificationId);
    } catch (error) {
      console.error('Failed to mark as read:', error);
      // server is source of truth on next hydrate; no rollback needed
    }
  };

  const handleMarkAllRead = async () => {
    dispatch(markAllRead());                // optimistic
    try {
      await tasksApi.markAllNotificationsRead();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleNotificationClick = (notification: TaskNotification) => {
    handleMarkRead(notification.id);
    navigate(`/tasks/${notification.task_id}`);
    setIsOpen(false);
  };

  const getNotificationIcon = (kind: string) => {
    switch (kind) {
      case 'ASSIGNED': return '📋';
      case 'MENTIONED': return '@';
      case 'STATUS': return '🔄';
      case 'BLOCKED': return '🚫';
      case 'COMMENT': return '💬';
      case 'COMPLETED': return '✅';
      case 'DUE_SOON': return '⏰';
      case 'OVERDUE': return '⚠️';
      default: return '📌';
    }
  };

  const getNotificationColor = (kind: string) => {
    switch (kind) {
      case 'ASSIGNED': return 'bg-emerald-100 text-emerald-700';
      case 'MENTIONED': return 'bg-blue-100 text-blue-700';
      case 'STATUS': return 'bg-amber-100 text-amber-700';
      case 'BLOCKED': return 'bg-rose-100 text-rose-700';
      case 'COMMENT': return 'bg-indigo-100 text-indigo-700';
      case 'COMPLETED': return 'bg-emerald-100 text-emerald-700';
      case 'DUE_SOON': return 'bg-orange-100 text-orange-700';
      case 'OVERDUE': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min ago`;
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-1.5 sm:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>

        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] sm:min-w-[20px] sm:h-5 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-full flex items-center justify-center px-1 animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dialog Box - Centered Modal Appearance */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300"
            onClick={() => setIsOpen(false)}
          />

          {/* Modal Dialog */}
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
              <div
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[95%] sm:max-w-lg md:max-w-2xl max-h-[85vh] sm:max-h-[80vh] overflow-hidden animate-in fade-in zoom-in duration-300"
                ref={dropdownRef}
              >
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 px-4 sm:px-6 py-4 sm:py-5">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900">Notifications</h2>
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Stay updated with your tasks</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Notifications List */}
                <div className="overflow-y-auto max-h-[calc(85vh-140px)] sm:max-h-[calc(80vh-150px)]">
                  {loading ? (
                    <div className="flex justify-center items-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <svg className="w-16 h-16 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <p className="text-sm font-medium">No notifications</p>
                      <p className="text-xs mt-1">You're all caught up!</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className={`p-4 sm:p-5 cursor-pointer transition-all hover:bg-gray-50 ${!notification.is_read ? 'bg-blue-50/30' : ''
                            }`}
                        >
                          <div className="flex gap-3 sm:gap-4">
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-lg sm:text-xl flex-shrink-0 ${getNotificationColor(notification.kind)}`}>
                              {getNotificationIcon(notification.kind)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start gap-2">
                                <h4 className={`text-sm sm:text-base font-medium ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                                  {notification.title}
                                </h4>
                                {!notification.is_read && (
                                  <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5"></span>
                                )}
                              </div>
                              <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">
                                {notification.body}
                              </p>
                              <div className="flex justify-between items-center mt-2">
                                <span className="text-xs text-gray-400">
                                  {formatTime(notification.created_at)}
                                </span>
                                {!notification.is_read && (
                                  <span className="text-xs text-blue-600 font-medium">New</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer with View All Notifications button at bottom right */}
                {notifications.length > 0 && (
                  <div className="sticky bottom-0 bg-white border-t border-gray-200 p-3 sm:p-4">
                    <div className="flex justify-end">
                      <button
                        onClick={async () => {
                          await handleMarkAllRead();
                          setIsOpen(false);
                        }}
                        className="px-4 sm:px-5 py-2 sm:py-2.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <span>Mark as Read</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes zoomIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-in {
          animation-duration: 0.2s;
          animation-fill-mode: both;
        }
        .fade-in {
          animation-name: fadeIn;
        }
        .zoom-in {
          animation-name: zoomIn;
        }
        
        /* Responsive line clamp for text */
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        /* Truncate text */
        .truncate {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
}